<template>
  <section class="dp-layout parking-config-page">
    <section class="dp-right">
      <div class="cfg-header">
        <div class="cfg-title">
          <span class="cfg-title-bar"></span>
          <h3>停车场配置</h3>
          <el-tag v-if="schoolName" type="success" size="small" effect="plain" class="cfg-school">
            <i class="el-icon-school"></i> {{ schoolName }}
          </el-tag>
        </div>
        <span class="cfg-status" :class="form.enabled ? 'is-on' : 'is-off'">
          <i class="cfg-status-dot"></i>{{ form.enabled ? '对接已启用' : '对接未启用' }}
        </span>
      </div>

      <!-- 页签只负责切换，内容在下方独立滚动区，避免全局 tabs 布局裁切表单 -->
      <el-tabs v-model="activeTab" class="cfg-tabs">
        <el-tab-pane label="对接配置" name="integration"></el-tab-pane>
        <el-tab-pane label="车牌配置" name="plate"></el-tab-pane>
        <el-tab-pane label="临时车配置" name="temp"></el-tab-pane>
      </el-tabs>

      <div class="cfg-scroll" v-loading="activeTab === 'integration' ? loading : businessLoading">
        <!-- 对接配置 -->
        <el-form
          v-show="activeTab === 'integration'"
          ref="configForm"
          :model="form"
          :rules="rules"
          label-width="150px"
          size="small"
          class="cfg-grid"
        >
          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-connection"></i>基础连接</div>
              <div class="cfg-section-desc">车场编号与密钥由车场系统提供</div>
            </div>
            <el-form-item label="启用停车场对接">
              <el-switch v-model="form.enabled"></el-switch>
            </el-form-item>
            <el-form-item label="车场编号" prop="parkId">
              <el-input v-model="form.parkId" placeholder="park_id" :disabled="!form.enabled"></el-input>
            </el-form-item>
            <el-form-item label="密钥" prop="ukey">
              <el-input
                v-model="form.ukey"
                placeholder="ukey"
                :disabled="!form.enabled"
                :type="showUkey ? 'text' : 'password'"
                autocomplete="new-password"
              >
                <i
                  slot="suffix"
                  :class="showUkey ? 'el-icon-view' : 'el-icon-minus'"
                  class="cfg-eye"
                  @click="showUkey = !showUkey"
                ></i>
              </el-input>
            </el-form-item>
            <el-form-item label="API 基础地址" prop="apiBaseUrl">
              <el-input v-model="form.apiBaseUrl" placeholder="http://istparking.sciseetech.com" :disabled="!form.enabled"></el-input>
              <div class="cfg-tip">默认无需修改，联调时由车场系统提供</div>
            </el-form-item>
          </div>

          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-refresh"></i>同步策略</div>
              <div class="cfg-section-desc">控制免费车何时下发到车场系统</div>
            </div>
            <el-form-item label="自动同步">
              <el-switch v-model="form.autoSync" :disabled="!form.enabled"></el-switch>
              <div class="cfg-tip">新增/编辑/删除立即下发；关闭后需在免费车管理中「批量同步」</div>
            </el-form-item>
            <el-form-item label="监护人车牌自动同步">
              <el-switch v-model="form.autoMirrorParent" :disabled="!form.enabled"></el-switch>
              <div class="cfg-tip">监护人维护车牌后自动加入/更新免费车</div>
            </el-form-item>
            <el-form-item label="教师车牌自动同步">
              <el-switch v-model="form.autoMirrorTeacher" :disabled="!form.enabled"></el-switch>
              <div class="cfg-tip">教师维护车牌后自动加入/更新免费车</div>
            </el-form-item>
            <el-form-item label="源数据移除时清理">
              <el-switch v-model="form.autoCleanupOnSourceRemoved" :disabled="!form.enabled"></el-switch>
              <div class="cfg-tip">换牌、删车牌、学生毕业等场景，自动移除旧牌并通知车场系统</div>
            </el-form-item>
          </div>

          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-date"></i>默认有效期</div>
              <div class="cfg-section-desc">导入/新增免费车未单独设置时使用</div>
            </div>
            <el-form-item label="免费类型">
              <el-radio-group v-model="form.endTypeLong" :disabled="!form.enabled">
                <el-radio-button :label="true">长期</el-radio-button>
                <el-radio-button :label="false">限期</el-radio-button>
              </el-radio-group>
              <div class="cfg-tip">{{ form.endTypeLong ? '长期车牌不设到期时间' : '限期车牌到期后自动失效' }}</div>
            </el-form-item>
            <template v-if="!form.endTypeLong">
              <el-form-item label="有效开始时间" prop="bTime">
                <el-date-picker v-model="form.bTime" type="datetime" placeholder="选择开始时间"
                  value-format="yyyy-MM-dd HH:mm:ss" style="width:100%" :disabled="!form.enabled"></el-date-picker>
              </el-form-item>
              <el-form-item label="有效结束时间" prop="eTime">
                <el-date-picker v-model="form.eTime" type="datetime" placeholder="选择结束时间"
                  value-format="yyyy-MM-dd HH:mm:ss" style="width:100%" :disabled="!form.enabled"></el-date-picker>
              </el-form-item>
            </template>
          </div>

          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-link"></i>回调地址</div>
              <div class="cfg-section-desc">车场系统推送进出记录的接收地址</div>
            </div>
            <el-form-item label="回调地址">
              <div class="cfg-callback">
                <span class="cfg-callback-url" :title="callbackUrl">{{ callbackUrl }}</span>
                <el-button type="text" icon="el-icon-document-copy" @click="copyCallbackUrl">复制</el-button>
              </div>
              <div class="cfg-tip is-warning">
                将该地址完整填写到车场系统的「回调推送」配置中
              </div>
            </el-form-item>
          </div>
        </el-form>

        <!-- 车牌配置 -->
        <el-form v-show="activeTab === 'plate'" :model="entryConfig" label-width="170px" size="small" class="cfg-grid">
          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-user"></i>家长长期车牌</div>
              <div class="cfg-section-desc">家长在 H5 录入，或班主任代录</div>
            </div>
            <el-form-item label="每生车牌数量">
              <el-input-number v-model="entryConfig.parentPlateMaxCount" :min="1" :max="100" controls-position="right"></el-input-number>
              <div class="cfg-tip">按学生统计所有监护人合计额度，默认 4 辆</div>
            </el-form-item>
            <el-form-item label="需要班主任审批">
              <el-switch v-model="entryConfig.parentPlateNeedApprove"></el-switch>
              <div class="cfg-tip">仅控制长期车牌，关闭后家长提交即生效；临时车审批不受此开关影响</div>
            </el-form-item>
          </div>

          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-s-custom"></i>教师长期车牌</div>
              <div class="cfg-section-desc">教师在 H5 录入本人车牌</div>
            </div>
            <el-form-item label="每人车牌数量">
              <el-input-number v-model="entryConfig.teacherPlateMaxCount" :min="1" :max="4" controls-position="right"></el-input-number>
              <div class="cfg-tip">最多 4 辆，默认 4 辆</div>
            </el-form-item>
            <el-form-item label="需要管理员审批">
              <el-switch v-model="entryConfig.teacherPlateNeedApprove"></el-switch>
              <div class="cfg-tip">由停车场管理员审批；关闭后提交即生效</div>
            </el-form-item>
          </div>
        </el-form>

        <!-- 临时车配置 -->
        <el-form v-show="activeTab === 'temp'" :model="entryConfig" label-width="170px" size="small" class="cfg-grid">
          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-time"></i>家长临时车</div>
              <div class="cfg-section-desc">家长为临时来访车辆申请短期通行</div>
            </div>
            <el-form-item label="开放申请">
              <el-switch v-model="entryConfig.tempVehicleEnabled"></el-switch>
              <div class="cfg-tip">家长临时车必须审批，审批通过后才下发道闸。请先在「临时车管理 → 审批流程设置」配置审批人（班主任/指定人员），未配置时家长无法提交</div>
            </el-form-item>
            <el-form-item label="每生同时有效数量">
              <el-input-number v-model="entryConfig.tempVehicleMaxCount" :min="1" :max="100" controls-position="right"
                :disabled="!entryConfig.tempVehicleEnabled"></el-input-number>
              <div class="cfg-tip">待审批与已生效且未到期的申请合计</div>
            </el-form-item>
            <el-form-item label="最大时长（天）">
              <el-input-number v-model="entryConfig.tempVehicleMaxDays" :min="1" :max="365" controls-position="right"
                :disabled="!entryConfig.tempVehicleEnabled"></el-input-number>
              <div class="cfg-tip">到期后自动回收，并从车场白名单移除</div>
            </el-form-item>
          </div>

          <div class="cfg-section">
            <div class="cfg-section-head">
              <div class="cfg-section-title"><i class="el-icon-share"></i>访客临时车</div>
              <div class="cfg-section-desc">管理员生成邀请链接/二维码，访客自助登记</div>
            </div>
            <el-form-item label="开放访客邀请">
              <el-switch v-model="entryConfig.visitorTempEnabled"></el-switch>
              <div class="cfg-tip">访客临时车不走审批：由停车场管理员生成邀请即视为授权，访客提交后直接生效并下发道闸。可通过邀请的有效期、使用次数控制，必要时在「邀请记录」中禁用</div>
            </el-form-item>
            <el-form-item label="邀请最大时长（天）">
              <el-input-number v-model="entryConfig.visitorInviteMaxDays" :min="1" :max="365" controls-position="right"
                :disabled="!entryConfig.visitorTempEnabled"></el-input-number>
              <div class="cfg-tip">生成邀请时可选的最长有效天数</div>
            </el-form-item>
            <el-form-item label="访客 H5 地址">
              <el-input
                v-model.trim="entryConfig.visitorH5Url"
                placeholder="如：https://h5.xxx.com/pages/parking/visitor-temp"
                maxlength="500"
                clearable
                :disabled="!entryConfig.visitorTempEnabled"
              ></el-input>
              <div class="cfg-tip">由 H5 前端提供的访客登记页地址，邀请链接 = 该地址 + ?token=…；未配置时只能生成邀请，不能复制链接/二维码</div>
            </el-form-item>
          </div>
        </el-form>
      </div>

      <div v-if="footerVisible" class="cfg-footer">
        <span class="cfg-footer-tip">{{ activeTab === 'integration' ? '修改后需点击保存才会生效' : '车牌配置与临时车配置一起保存' }}</span>
        <el-button
          v-if="activeTab === 'integration'"
          type="primary"
          icon="el-icon-check"
          :loading="saving"
          :disabled="!schoolId"
          @click="save"
        >保存对接配置</el-button>
        <el-button
          v-else
          type="primary"
          icon="el-icon-check"
          :loading="businessSaving"
          :disabled="!schoolId"
          @click="saveBusinessConfig"
        >保存业务配置</el-button>
      </div>
    </section>
  </section>
</template>

<script>
import {
  getParkingConfig,
  saveParkingConfig,
  getParkingVehicleEntryConfig,
  saveParkingVehicleEntryConfig
} from '@/api/parking'
import { BaseApiUrl } from '@/api/base'
import { loadButtonFuncs } from './parkingUtils'

export default {
  name: 'ParkingConfig',
  components: {},
  data() {
    return {
      buttonFuncs: [],
      schoolId: null,
      schoolName: '',
      activeTab: 'integration',
      showUkey: false,
      loading: false,
      saving: false,
      businessLoading: false,
      businessSaving: false,
      entryConfig: {
        parentPlateMaxCount: 4,
        parentPlateNeedApprove: false,
        teacherPlateMaxCount: 4,
        teacherPlateNeedApprove: false,
        tempVehicleEnabled: false,
        tempVehicleMaxCount: 1,
        tempVehicleMaxDays: 7,
        visitorTempEnabled: false,
        visitorInviteMaxDays: 7,
        visitorH5Url: ''
      },
      form: {
        enabled: false,
        parkId: '',
        ukey: '',
        apiBaseUrl: 'http://istparking.sciseetech.com',
        autoSync: true,
        autoMirrorParent: false,
        autoMirrorTeacher: false,
        autoCleanupOnSourceRemoved: true,
        endTypeLong: true,
        bTime: null,
        eTime: null
      },
      rules: {
        parkId: [{ required: true, message: '请输入车场编号', trigger: 'blur' }],
        ukey: [{ required: true, message: '请输入密钥', trigger: 'blur' }]
      }
    }
  },
  computed: {
    /** 底部保存栏：按当前页签对应的按钮权限显示 */
    footerVisible() {
      return this.activeTab === 'integration' ? this.can('save') : this.can('saveBusinessConfig')
    },
    /** 完整回调地址：按构建环境的 VUE_APP_BASE_API 自动拼接（开发=本机，生产=正式域名） */
    callbackUrl() {
      return `${String(BaseApiUrl || '').replace(/\/+$/, '')}/api/BaishengParkingCallback/Callback`
    }
  },
  watch: {
    '$store.state.activeSchoolId': {
      handler(newVal) {
        if (newVal) {
          this.schoolId = newVal
          this.schoolName = this.$store.state.activeSchoolName
          this.loadConfig()
          this.loadBusinessConfig()
        }
      },
      immediate: true
    }
  },
  created() {
    this.buttonFuncs = loadButtonFuncs(this.$route.path)
  },
  methods: {
    /** 当前角色是否分配了该按钮权限 */
    can(func) {
      return this.buttonFuncs.includes(func)
    },
    async loadConfig() {
      if (!this.schoolId) return
      this.loading = true
      try {
        const res = await getParkingConfig(this.schoolId)
        if (res.data && res.data.success && res.data.response) {
          const c = res.data.response
          // 后端返回 PascalCase，兼容两种格式
          this.form = {
            enabled: c.Enabled !== undefined ? !!c.Enabled : !!c.enabled,
            parkId: c.ParkId || c.parkId || '',
            ukey: c.Ukey || c.ukey || '',
            apiBaseUrl: c.ApiBaseUrl || c.apiBaseUrl || 'http://istparking.sciseetech.com',
            autoSync: (c.AutoSync !== undefined ? c.AutoSync : c.autoSync) !== false,
            autoMirrorParent: !!(c.AutoMirrorParent !== undefined ? c.AutoMirrorParent : c.autoMirrorParent),
            autoMirrorTeacher: !!(c.AutoMirrorTeacher !== undefined ? c.AutoMirrorTeacher : c.autoMirrorTeacher),
            autoCleanupOnSourceRemoved: (c.AutoCleanupOnSourceRemoved !== undefined ? c.AutoCleanupOnSourceRemoved : c.autoCleanupOnSourceRemoved) !== false,
            endTypeLong: (c.EndType !== undefined ? c.EndType : c.endType) !== 0,
            bTime: c.BTime || c.bTime || null,
            eTime: c.ETime || c.eTime || null
          }
        }
      } catch (e) {
        this.$message.error('加载配置失败')
      } finally {
        this.loading = false
      }
    },
    async loadBusinessConfig() {
      if (!this.schoolId) return
      this.businessLoading = true
      try {
        const res = await getParkingVehicleEntryConfig(this.schoolId)
        const c = (res.data && res.data.response) || {}
        const value = key => c[key] !== undefined ? c[key] : c[key.charAt(0).toLowerCase() + key.slice(1)]
        this.entryConfig = {
          parentPlateMaxCount: value('ParentPlateMaxCount') || 4,
          parentPlateNeedApprove: !!value('ParentPlateNeedApprove'),
          teacherPlateMaxCount: value('TeacherPlateMaxCount') || 4,
          teacherPlateNeedApprove: !!value('TeacherPlateNeedApprove'),
          tempVehicleEnabled: !!value('TempVehicleEnabled'),
          tempVehicleMaxCount: value('TempVehicleMaxCount') || 1,
          tempVehicleMaxDays: value('TempVehicleMaxDays') || 7,
          visitorTempEnabled: !!value('VisitorTempEnabled'),
          visitorInviteMaxDays: value('VisitorInviteMaxDays') || 7,
          visitorH5Url: value('VisitorH5Url') || ''
        }
      } catch (e) {
        this.$message.error('加载业务配置失败')
      } finally {
        this.businessLoading = false
      }
    },
    async saveBusinessConfig() {
      if (!this.schoolId) return
      const h5Url = this.entryConfig.visitorH5Url
      if (h5Url && !/^https?:\/\/\S+$/i.test(h5Url)) {
        this.$message.warning('访客 H5 地址须以 http:// 或 https:// 开头')
        return
      }
      this.businessSaving = true
      try {
        const res = await saveParkingVehicleEntryConfig(this.schoolId, this.entryConfig)
        if (res.data && res.data.success) {
          this.$message.success('业务配置保存成功')
        } else {
          this.$message.error((res.data && res.data.msg) || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存业务配置失败')
      } finally {
        this.businessSaving = false
      }
    },
    save() {
      this.$refs.configForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const config = {
            enabled: this.form.enabled,
            parkId: this.form.parkId,
            ukey: this.form.ukey,
            apiBaseUrl: this.form.apiBaseUrl,
            autoSync: this.form.autoSync,
            autoMirrorParent: this.form.autoMirrorParent,
            autoMirrorTeacher: this.form.autoMirrorTeacher,
            autoCleanupOnSourceRemoved: this.form.autoCleanupOnSourceRemoved,
            endType: this.form.endTypeLong ? 1 : 0,
            bTime: this.form.endTypeLong ? null : this.form.bTime,
            eTime: this.form.endTypeLong ? null : this.form.eTime
          }
          const res = await saveParkingConfig(this.schoolId, config)
          if (res.data && res.data.success) {
            this.$message.success('保存成功')
          } else {
            this.$message.error(res.data && res.data.msg || '保存失败')
          }
        } catch (e) {
          this.$message.error('保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    copyCallbackUrl() {
      navigator.clipboard.writeText(this.callbackUrl).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.warning('复制失败，请手动复制')
      })
    }
  }
}
</script>

<style>
/* 停车场配置页：样式全部以 .parking-config-page 为前缀，避免污染其他页面。最后修改：2026-10-10 */
.parking-config-page > .dp-right {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
  padding: 0;
}

/* 页头 */
.parking-config-page .cfg-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px 0;
}
.parking-config-page .cfg-title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.parking-config-page .cfg-title-bar {
  width: 4px;
  height: 16px;
  border-radius: 2px;
  background: #2563eb;
}
.parking-config-page .cfg-title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}
.parking-config-page .cfg-school {
  border-radius: 6px;
}
.parking-config-page .cfg-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}
.parking-config-page .cfg-status.is-on {
  color: #047857;
  background: #ecfdf5;
}
.parking-config-page .cfg-status.is-off {
  color: #64748b;
  background: #f1f5f9;
}
.parking-config-page .cfg-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* 页签：仅作切换条 */
.parking-config-page .cfg-tabs {
  flex-shrink: 0;
  margin: 0 !important;
  padding: 0 20px;
}
.parking-config-page .cfg-tabs > .el-tabs__header {
  margin: 0;
}
.parking-config-page .cfg-tabs .el-tabs__item {
  height: 44px;
  line-height: 44px;
  font-size: 14px;
}
.parking-config-page .cfg-tabs > .el-tabs__content {
  display: none;
}

/* 内容滚动区 */
.parking-config-page .cfg-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px 20px;
  background: #f8fafc;
}
.parking-config-page .cfg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(460px, 1fr));
  gap: 16px;
  align-items: start;
  max-width: 1280px;
}

/* 分组卡片 */
.parking-config-page .cfg-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 20px 4px;
}
.parking-config-page .cfg-section-head {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}
.parking-config-page .cfg-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.parking-config-page .cfg-section-title i {
  color: #2563eb;
  font-size: 16px;
}
.parking-config-page .cfg-section-desc {
  margin-top: 4px;
  padding-left: 24px;
  font-size: 12px;
  color: #64748b;
}

/* 表单 */
.parking-config-page .cfg-section .el-form-item {
  margin-bottom: 16px;
}
.parking-config-page .cfg-section .el-form-item__label {
  color: #475569;
  font-weight: 500;
}
.parking-config-page .cfg-section .el-input,
.parking-config-page .cfg-section .el-date-editor.el-input {
  max-width: 320px;
}
.parking-config-page .cfg-section .el-input-number {
  width: 140px;
}
.parking-config-page .el-input.is-disabled .el-input__inner {
  background-color: #f8fafc;
  color: #94a3b8;
}
.parking-config-page .cfg-eye {
  cursor: pointer;
  line-height: 32px;
}
.parking-config-page .cfg-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.6;
  color: #64748b;
}
.parking-config-page .cfg-tip.is-warning {
  color: #b45309;
}

/* 回调地址 */
.parking-config-page .cfg-callback {
  display: flex;
  align-items: center;
  padding: 0 4px 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
}
.parking-config-page .cfg-callback-url {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  color: #334155;
}

/* 底部固定操作栏 */
.parking-config-page .cfg-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 10px 20px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}
.parking-config-page .cfg-footer-tip {
  font-size: 12px;
  color: #94a3b8;
}

@media screen and (max-width: 768px) {
  .parking-config-page .cfg-grid {
    grid-template-columns: 1fr;
  }
  .parking-config-page .cfg-section .el-input {
    max-width: none;
  }
}
</style>
